import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import { Link } from 'react-router-dom';

const adjectives = ['quality', 'reliable', 'expert', 'innovative', 'superior'];

export function HeroSection() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % adjectives.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const { data: dynStats } = useQuery({
    queryKey: ['portfolioStats'],
    queryFn: async () => {
      const { data } = await api.get('portfolio-stats/');
      return data;
    }
  });

  const heroStats = [
    [dynStats?.years_experience || '15+', 'Years'],
    [dynStats?.projects_completed || '500+', 'Projects'],
    [dynStats?.client_satisfaction || '100%', 'Satisfaction']
  ];

  return (
    <section className="relative w-full overflow-hidden min-h-[60vh] sm:min-h-[70vh] lg:min-h-[75vh] flex items-center">
      {/* Background Image with Multi-Layer Overlay */}
      <div className="absolute inset-0">
        <img
          src="/assets/Hero%20Back.png"
          alt="Construction background"
          className="h-full w-full object-cover scale-105"
        />
        {/* Layered gradient overlays for depth */}
        <div className="absolute inset-0 bg-linear-to-r from-slate-950/90 via-slate-900/60 to-transparent" />
        <div className="absolute inset-0 bg-linear-to-t from-slate-950/80 via-transparent to-transparent" />
        {/* Subtle noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIzMDAiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMSIvPjwvc3ZnPg==')]" />
      </div>

      {/* Decorative amber accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-linear-to-b from-transparent via-amber-500 to-transparent opacity-60 z-10" />

      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 text-left pt-[50px] sm:pt-[70px] lg:pt-[90px] pb-[50px] sm:pb-[60px] lg:pb-[70px]">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.23, 1, 0.32, 1] }}
          className="max-w-4xl"
        >
          {/* Eyebrow label */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6"
          >
            <div className="w-6 sm:w-8 h-px bg-accent" />
            <span className="text-amber-300 text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] sm:tracking-[0.3em]">Excellence in Construction</span>
          </motion.div>

          <h1
            className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold text-white drop-shadow-2xl mb-4 sm:mb-6 text-left leading-[1.1] sm:leading-[1.05]"
          >
            <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-4 md:gap-x-6">
              <span className="whitespace-nowrap">Building trust through</span>
              <span className="relative inline-flex items-center min-w-[100px] sm:min-w-40 md:min-w-60 lg:min-w-95 h-[1.2em] text-amber-300 overflow-hidden drop-shadow-[0_0_30px_rgba(251,191,36,0.4)]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={adjectives[index]}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -40 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="absolute left-0"
                  >
                    {adjectives[index]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </div>
            <div className="mt-1 sm:mt-2 md:mt-4">construction</div>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-sm sm:text-lg md:text-xl text-slate-100/85 max-w-2xl mb-6 sm:mb-8 md:mb-10 leading-relaxed"
          >
            Delivering high-quality construction solutions for residential, commercial, and industrial projects with precision and excellence.
          </motion.p>

          {/* Dual CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-wrap gap-3 sm:gap-4"
          >
            <Link to="/contact">
              <button className="relative group bg-accent text-slate-900 font-black text-xs sm:text-sm md:text-base px-5 sm:px-8 md:px-10 py-3 sm:py-4 hover:bg-amber-400 transition-all flex items-center gap-2 uppercase tracking-widest shadow-[0_0_40px_rgba(251,191,36,0.35)] hover:shadow-[0_0_60px_rgba(251,191,36,0.55)] hover:-translate-y-1 overflow-hidden">
                <span className="relative z-10">Request Free Visitation</span>
                <div className="absolute inset-0 bg-white/20 translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
              </button>
            </Link>
          </motion.div>

        </motion.div>
      </div>

      {/* Scroll Down Indicator */}
      <motion.div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center cursor-pointer group"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
      >
        <motion.div
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          className="border-2 border-white/40 rounded-full p-2 group-hover:border-white/80 transition-colors"
        >
          <ChevronDown className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
        </motion.div>
      </motion.div>
    </section>
  );
}
